import * as React from 'react';

import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { BaseParams } from '@common/react/objects/BaseParams';

export interface SuccessProps<T, ModalProps extends BaseParams = BaseParams> {
	res: T;
	success: (message?: string | React.ReactNode, props?: ModalProps) => void;
	error: (message?: string, props?: ModalProps) => void;
}

interface ChildrenOptions {
	onClick: (e?) => void;
	isLoading: boolean;
}

export interface LoadingWrapperProps<TResponse, ModalProps extends BaseParams = BaseParams> {
	actionFunction: () => Promise<TResponse>;
	children: (params: ChildrenOptions) => React.ReactNode;
	/*
	 * if we don't have it or it return true actionFunction will be called
	 */
	beforeAction?: (action, event) => boolean;
	successMessage?: string;
	errorMessage?: string;
	onSuccess?: (props: SuccessProps<TResponse, ModalProps>) => void;
	onError?: (err: string) => void;
	/*
	 * by default false, so we show error from actionFunction
	 * if it make request, we show server error
	 */
	hideActionErrorMessage?: boolean;
	/*
	 * use 'messages' for success and error showing, by default false, so we use 'modals'
	 */
	useMessages?: boolean;
	confirmationProps?: ModalProps;
}

const LoadingWrapper: <TResponse>(
	props: LoadingWrapperProps<TResponse>,
) => React.ReactElement = <TResponse, >(
	props: LoadingWrapperProps<TResponse>,
) => {
	const modalContext = useModal();
	const {
		actionFunction,
		successMessage = 'Success.',
		errorMessage = 'Sorry, something went wrong.',
		hideActionErrorMessage = false,
		useMessages = false,
		children,
		confirmationProps,
		beforeAction = confirmationProps
			? (action): boolean => {
				modalContext.openConfirm({
					content: '',
					okText: 'Yes',
					okType: 'primary',
					cancelText: 'No',
					...confirmationProps,
					onOk: action,
				});
				return false;
			}
			: () => true,
	} = props;

	const defaultOnSuccess = useMessages
		? (message, props?) => modalContext.openSuccessMessage?.(typeof message === 'string' ? message || successMessage : successMessage, props)
		: (message, props?) => modalContext.openSuccess({
			title: 'Success',
			content: typeof message === 'string' ? message || successMessage : successMessage,
			...props,
		});

	const defaultOnError = useMessages
		? (message, props?) => modalContext.openErrorMessage?.(message || errorMessage, props)
		: (message, props?) => modalContext.openError({
			title: 'Error',
			content: message || errorMessage,
			...props,
		});

	const onSuccess = props.onSuccess || defaultOnSuccess;
	const onError = props.onError || defaultOnError;
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const callAction = () => {
		setIsLoading(true);

		actionFunction()
			.then((res: TResponse) => {
				onSuccess({ res, success: defaultOnSuccess, error: defaultOnError });
			})
			.catch((err: string) => {
				onError(hideActionErrorMessage ? errorMessage : err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (beforeAction) {
			if (beforeAction(callAction, event)) {
				callAction();
			}
		} else {
			callAction();
		}
	};

	return <>
		{children({ onClick, isLoading })}
	</>;
};

export default LoadingWrapper;
